import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { isEmpty } from 'lodash-es';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  [param: string]: any;
};

const useStyles = makeStyles()(() => ({
  inputAdornment: {
    marginRight: 0,
  },
  searchIcon: {
    position: 'relative',
    left: '-4px',
    marginRight: '4px',
  },
}));

export function CustomSearchInput({
  value,
  disabled = false,
  fullWidth = false,
  placeholder,
  onChange,
  onEnter,
  onClear,
  ...rest
}: Props) {
  const { classes } = useStyles();

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange(event.target.value),
    [onChange]
  );

  const handleEnterKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && onEnter && !disabled) {
        event.preventDefault();
        onEnter?.();
      }
    },
    [disabled, onEnter]
  );

  const handleClear = React.useCallback(() => {
    onChange('');
    onClear?.();
  }, [onChange, onClear]);

  return (
    <TextField
      aria-label="custom search input"
      size="small"
      hiddenLabel
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onKeyUp={handleEnterKey}
      disabled={disabled}
      fullWidth={fullWidth}
      slotProps={{
        input: {
          sx: { borderRadius: 1 },
          startAdornment: (
            <InputAdornment position="start" className={classes.inputAdornment}>
              <SearchIcon className={classes.searchIcon} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search query"
                edge="end"
                onClick={handleClear}
                disabled={isEmpty(value) || disabled}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  );
}
